import type { LucideIcon } from 'lucide-react'
import { useRef } from 'react'

import { footerTabId, footerTabPanelId } from '@/components/shared/footer-tab-bar.utils'
import { FOOTER_HEIGHT } from '@/lib/constants'
import { cn } from '@/lib/utils'

export type FooterTabSpec<T extends string> = {
  value: T
  label: string
  icon: LucideIcon
  count?: number | null
}

type FooterTabBarProps<T extends string> = {
  /** tab と tabpanel を紐づける id の接頭辞。画面内で一意な文字列を渡す */
  idPrefix: string
  tabs: readonly FooterTabSpec<T>[]
  activeValue: T
  onChange: (value: T) => void
  ariaLabel: string
}

/**
 * FooterTabBar - 画面下部固定のタブ切り替えバー
 *
 * library-search / todo 画面などで使われていた重複インライン実装を共通化したもの。
 * WAI-ARIA の tab パターンに従い、ロービング tabindex と矢印キー / Home / End 操作に対応する。
 */
export const FooterTabBar = <T extends string>({
  idPrefix,
  tabs,
  activeValue,
  onChange,
  ariaLabel,
}: FooterTabBarProps<T>) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = tabs.length - 1
    const nextIndex = ((): number | null => {
      if (event.key === 'ArrowRight') return index === lastIndex ? 0 : index + 1
      if (event.key === 'ArrowLeft') return index === 0 ? lastIndex : index - 1
      if (event.key === 'Home') return 0
      if (event.key === 'End') return lastIndex
      return null
    })()

    if (nextIndex === null) return

    const nextTab = tabs.at(nextIndex)
    if (!nextTab) return

    event.preventDefault()
    tabRefs.current.at(nextIndex)?.focus()
    onChange(nextTab.value)
  }

  return (
    <div className="shrink-0 border-t bg-background/80 pb-[env(safe-area-inset-bottom)] backdrop-blur">
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="grid"
        style={{ height: FOOTER_HEIGHT, gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
      >
        {tabs.map(({ value, label, icon: Icon, count }, index) => {
          const isActive = activeValue === value
          return (
            <button
              key={value}
              ref={(el) => {
                tabRefs.current[index] = el
              }}
              type="button"
              role="tab"
              id={footerTabId(idPrefix, value)}
              aria-selected={isActive}
              aria-controls={footerTabPanelId(idPrefix)}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                // iOS のホームインジケータ領域でアイコン/ラベルが見切れないよう、
                // バーの高さは変えずに pb で中身だけ少し上寄せにする
                'flex flex-col items-center justify-center gap-0.5 pb-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" />
              <span className="text-[10px]">
                {label}
                {count != null && <span className="ml-1 opacity-70">({count})</span>}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
